import { shallowRef, type ShallowRef } from "vue";

import { CLOCK_SKEW_TOLERANCE_MS } from "@/composables/content-read-state";

import type { ContentfulItem } from "@tmi/types/entities";

/*****************************************************************************
 * New trade alerts, as they land (gap_alert_delivery_and_notifications-2).
 *
 * Application.applyCacheUpdate snapshots the trade-alert ids before each
 * re-read of the cache and hands this module the catalog after it. What is
 * new here is what NewAlertAnnouncer announces (a toast and a polite live
 * region). Plain module state, no Vue component or store: the store hook runs
 * whether or not the announcer is mounted, and the announcer only hears
 * batches recorded while it is.
 *
 * A diff of "newest publishedDate before and after" would announce a whole
 * catalog: a cold cache, a change of member, a re-hydration, a catch-up after
 * the tab slept. So an alert is announced only when all of these hold:
 * - the store already held alerts before this update (it was populated this
 *   session; an empty store is a first load, not news);
 * - its id was not in that snapshot and has not been announced before;
 * - it was published within NEW_TRADE_ALERT_MAX_AGE_MS (allowing the usual
 *   clock skew), so grants widening or a long-hidden tab catching up stay
 *   quiet.
 *
 * Every tab records the same arrival (the worker's update is relayed to the
 * member's other tabs), so which alerts have been shown is shared: the tab that
 * shows one records it in localStorage (recordTradeAlertsAnnounced), and a
 * tab holding it for later drops it (announcedElsewhere). Recording arrivals
 * there instead would make each tab silence every other one.
 *****************************************************************************/

/** How recent an alert must be to be announced as it arrives. */
export const NEW_TRADE_ALERT_MAX_AGE_MS = 15 * 60 * 1000;
/**
 * The alerts any of the member's tabs has shown, as { id: publishedAt }. User-scoped (USER_SCOPED_PURGE_KEYS), and
 * pruned like the per-tab record, so it never holds more than the last few minutes of alerts.
 */
export const ANNOUNCED_TRADE_ALERTS_STORAGE_KEY = "new-trade-alerts-announced";

export interface TradeAlertArrival {
    /** The newly arrived alerts, newest first. Never empty. */
    alerts: readonly ContentfulItem[];
    /** When they were recorded (epoch ms), so two batches of the same alerts are still two changes. */
    at: number;
}

const arrival: ShallowRef< TradeAlertArrival | null > = shallowRef( null );
// Every id announced this session, with its publish time, so a re-read racing another (the worker and a relayed tab)
// cannot announce the same alert twice. Pruned once an alert is too old to be announced again anyway.
const announced = new Map< string, number >();

/** The latest batch of new trade alerts; watch it. null until the first arrival. */
export const tradeAlertArrival: Readonly< ShallowRef< TradeAlertArrival | null > > = arrival;

/** The ids a catalog holds now, taken before the store re-reads the cache. */
export function tradeAlertIdSnapshot( alerts: readonly ContentfulItem[] | undefined ): ReadonlySet< string > {
    return new Set( ( alerts ?? [] ).map( ( alert: ContentfulItem ) => alert.contentfulId ) );
}

function publishedAt( alert: ContentfulItem ): number {
    return alert.publishedDate ? new Date( alert.publishedDate ).getTime() : Number.NaN;
}

/** Whether an alert is recent enough to be announced now (the usual clock skew allowed both ways). */
export function isAnnounceableTradeAlert( alert: ContentfulItem, now: number = Date.now() ): boolean {
    const age = now - publishedAt( alert );
    return Number.isFinite( age ) && age <= NEW_TRADE_ALERT_MAX_AGE_MS && age >= -CLOCK_SKEW_TOLERANCE_MS;
}

/** Too old to be announced by any tab, so no longer worth remembering. */
function isExpired( publishedAtMs: number, now: number ): boolean {
    return !Number.isFinite( publishedAtMs ) || now - publishedAtMs > NEW_TRADE_ALERT_MAX_AGE_MS + CLOCK_SKEW_TOLERANCE_MS;
}

/** The shared record, pruned. Never throws: without localStorage each tab announces on its own, as before. */
function readAnnouncedElsewhere( now: number ): Record< string, number > {
    try {
        const raw: string | null | undefined = globalThis.localStorage?.getItem( ANNOUNCED_TRADE_ALERTS_STORAGE_KEY );
        const parsed: unknown = raw ? JSON.parse( raw ) : {};
        if ( !parsed || typeof parsed !== "object" || Array.isArray( parsed ) ) {
            return {};
        }

        const kept: Record< string, number > = {};
        Object.entries( parsed as Record< string, unknown > ).forEach( ( [ id, at ] ) => {
            if ( typeof at === "number" && !isExpired( at, now ) ) {
                kept[ id ] = at;
            }
        } );
        return kept;
    } catch {
        return {};
    }
}

/**
 * Records that this tab has shown these alerts, so the member's other tabs do not show them again when they come to
 * the front. Called when the toast is raised, not when an alert arrives.
 */
export function recordTradeAlertsAnnounced( alerts: readonly ContentfulItem[], now: number = Date.now() ): void {
    const state = readAnnouncedElsewhere( now );
    alerts.forEach( ( alert: ContentfulItem ) => {
        const at = publishedAt( alert );
        if ( alert.contentfulId && !isExpired( at, now ) ) {
            state[ alert.contentfulId ] = at;
        }
    } );

    try {
        globalThis.localStorage?.setItem( ANNOUNCED_TRADE_ALERTS_STORAGE_KEY, JSON.stringify( state ) );
    } catch {
        // localStorage unavailable (private mode, quota): the other tabs may repeat the toast, as before.
    }
}

/** A test for "one of the member's tabs has already shown this alert", reading the shared record once for a batch. */
export function announcedElsewhere( now: number = Date.now() ): ( alert: ContentfulItem ) => boolean {
    const state = readAnnouncedElsewhere( now );
    return ( alert: ContentfulItem ) => Object.prototype.hasOwnProperty.call( state, alert.contentfulId );
}

/**
 * Records the alerts `after` holds that are new since `before` (see the rules above) and publishes them as one batch.
 * Returns what was recorded, newest first.
 */
export function recordTradeAlertArrivals(
    before: ReadonlySet< string >,
    after: readonly ContentfulItem[] | undefined,
    now: number = Date.now(),
): ContentfulItem[] {
    announced.forEach( ( at: number, id: string ) => {
        if ( now - at > NEW_TRADE_ALERT_MAX_AGE_MS + CLOCK_SKEW_TOLERANCE_MS ) {
            announced.delete( id );
        }
    } );

    // An empty store is a first load for this member (or right after a reset): nothing in it is news.
    if ( before.size === 0 ) {
        announced.clear();
        return [];
    }

    const fresh = ( after ?? [] )
        .filter( ( alert: ContentfulItem ) => {
            const id = alert.contentfulId;
            return !!id
                && !before.has( id )
                && !announced.has( id )
                && isAnnounceableTradeAlert( alert, now );
        } )
        .sort( ( a: ContentfulItem, b: ContentfulItem ) => publishedAt( b ) - publishedAt( a ) );

    if ( fresh.length === 0 ) {
        return [];
    }

    fresh.forEach( ( alert: ContentfulItem ) => announced.set( alert.contentfulId, publishedAt( alert ) ) );
    arrival.value = { alerts: fresh, at: now };
    return fresh;
}

/** Test helper: forget every arrival and every announced id, this tab's and the shared record. */
export function resetTradeAlertArrivals(): void {
    announced.clear();
    arrival.value = null;
    try {
        globalThis.localStorage?.removeItem( ANNOUNCED_TRADE_ALERTS_STORAGE_KEY );
    } catch {
        // Nothing to forget.
    }
}
